import { useEffect } from 'react'
import styled from 'styled-components'

import { ExploreFilter } from '@root/features/explore/ui/explore-filter/desktop'
import { ExplorePairs } from '@root/features/explore/ui/explore-pairs/desktop/explore-pairs'

import { ExploreTableSection } from './explore-table-section'

/**
 * Fetch the chart while the visitor is still reading the list.
 *
 * The token page loads its chart through `lazy()`, so clicking a row starts
 * a chunk download before the chart can ask for a single candle. Measured on
 * staging, that is where the gap comes from: the page's other reads leave at
 * 476ms and `chart-price` does not leave until **1,687ms**, because
 * `trade-chart-base` is 840 KB and `echarts` another 607 KB behind it.
 *
 * Anybody on this screen is one click from a token page — it is the only
 * thing the screen is for — so the chunk is worth having before the click
 * rather than after it. Idle time, so it takes nothing from the list the
 * visitor is actually looking at, and a plain `import()` because the module
 * cache is what the click will hit.
 *
 * Desktop only: this page is the desktop tree, and the mobile chart is a
 * different module.
 */
const warmChart = () => {
  void import('@root/widgets/trade/trade-chart/ui/desktop/trade-chart').catch(
    () => undefined,
  )
}

export const ExplorePage: React.FC = () => {
  useEffect(() => {
    const idle = window.requestIdleCallback as
      | ((callback: () => void, options?: { timeout: number }) => number)
      | undefined

    if (!idle) {
      const timer = setTimeout(warmChart, 1500)
      return () => {
        clearTimeout(timer)
      }
    }

    // Measured on staging: with a four-second ceiling the main thread stayed
    // busy and the fetch did not start until 4,845ms, which is long after a
    // visitor could have clicked. The list is drawn by about a second, so
    // forcing it at 1,500ms costs that nothing and reliably beats the click.
    const handle = idle(warmChart, { timeout: 1500 })
    return () => {
      window.cancelIdleCallback?.(handle)
    }
  }, [])

  return (
    <Root>
      <ExplorePairs />
      <ExploreTableSection />
      <ExploreFilter />
    </Root>
  )
}

const Root = styled.div`
  --explore-pairs-body-height: 70vh;

  min-height: 0;
  min-width: 0;
  /* Match trade page: grow past the desktop scrollport so layout ScrollableArea scrolls (incl. wheel over sidebar). */
  min-height: 100vh;
  min-height: 100cqh;

  display: grid;
  grid-template-rows: auto auto;

  background: var(--appearance-1);
`
